import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell, CheckCircle2, AlertTriangle, AlertOctagon, Info,
  Briefcase, MessageSquare, Rocket, Fingerprint, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../services/api";

/**
 * Universal Notification Center (additive · V2.6)
 * ----------------------------------------------------------------
 * Dropdown panel attached to the top-bar bell icon.
 * Shows the 10 most recent notifications, with unread badge,
 * priority icon/color, relative timestamp and clickable link.
 * Polls /notifications every 60 s to keep the badge fresh and
 * emits a Sonner toast when a new "important" or "warning" notif
 * arrives while the panel is closed.
 */

const PRIORITY_META = {
  info:      { color: "#3B82F6", bg: "rgba(59,130,246,0.10)", Icon: Info,          label: "Info" },
  success:   { color: "#15803D", bg: "rgba(21,128,61,0.10)",  Icon: CheckCircle2,  label: "Succès" },
  warning:   { color: "#B45309", bg: "rgba(180,83,9,0.10)",   Icon: AlertTriangle, label: "À traiter" },
  important: { color: "#B91C1C", bg: "rgba(185,28,28,0.10)",  Icon: AlertOctagon,  label: "Important" },
};

const KIND_ICON = {
  "application.accepted": Briefcase,
  "application.refused": Briefcase,
  "application_received": Briefcase,
  "application_status": Briefcase,
  "project.validated": Rocket,
  "project.rejected": Rocket,
  "project.info_requested": Rocket,
  "project.invitation": Rocket,
  "profile.approved": Fingerprint,
  "profile.rejected": Fingerprint,
  "orchestrator_ready": Rocket,
  "orchestrator_expert": Rocket,
  "orchestrator.authorized": Rocket,
  "orchestrator.expert": Rocket,
  "message_received": MessageSquare,
  "message.important": MessageSquare,
  "admin_message": Info,
  "admin_announcement": Info,
  "assistant_escalation": Rocket,
};

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    if (diff < 60) return `il y a ${diff}s`;
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString("fr-FR");
  } catch { return ""; }
}

function extractPriority(n) {
  const p = n?.meta?.priority;
  if (p && PRIORITY_META[p]) return p;
  // heuristic fallback based on kind
  if (n?.kind?.includes("accepted") || n?.kind?.includes("validated") || n?.kind?.includes("approved") || n?.kind === "orchestrator_ready") return "success";
  if (n?.kind?.includes("rejected") || n?.kind?.includes("refused") || n?.kind?.includes("info_requested")) return "warning";
  if (n?.kind?.includes("invitation") || n?.kind === "orchestrator_expert" || n?.kind === "message.important") return "important";
  return "info";
}

function extractLink(n) {
  if (n?.meta?.link) return n.meta.link;
  if (n?.meta?.thread_id) return `/messages/${n.meta.thread_id}`;
  if (n?.meta?.offer_id) return `/offers/${n.meta.offer_id}`;
  if (n?.meta?.application_id) return "/applications";
  if (n?.meta?.project_id) return `/projects/${n.meta.project_id}`;
  if (n?.meta?.accompaniment_id) return "/mon-accompagnement";
  return "/notifications";
}

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const prevIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const navigate = useNavigate();

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const list = data.items || [];
      setItems(list);
      setUnread(data.unread || 0);

      // Detect newly-arrived important/warning notifs
      const prev = prevIdsRef.current;
      if (!firstLoadRef.current) {
        const fresh = list.filter((n) => !prev.has(n.id) && !n.read);
        const criticals = fresh.filter((n) => {
          const p = extractPriority(n);
          return p === "important" || p === "warning";
        });
        if (criticals.length > 0) {
          const first = criticals[0];
          const p = extractPriority(first);
          const meta = PRIORITY_META[p];
          const emit = p === "important" ? toast.error : toast.warning;
          emit(first.title || "Nouvelle notification", {
            description: (first.body || "").slice(0, 120),
            duration: 8000,
            action: {
              label: "Ouvrir",
              onClick: () => {
                markOne(first.id);
                navigate(extractLink(first));
              },
            },
            icon: meta ? <meta.Icon size={16} color={meta.color} /> : undefined,
          });
        }
      }
      prevIdsRef.current = new Set(list.map((n) => n.id));
      firstLoadRef.current = false;
    } catch {
      // silent — will retry
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    fetchNotifs();
    const id = setInterval(fetchNotifs, 60000);
    return () => clearInterval(id);
  }, [fetchNotifs]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const markOne = async (id) => {
    // optimistic
    setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((c) => Math.max(0, c - 1));
    try { await api.patch(`/notifications/${id}/read`); } catch { /* ignore */ }
  };

  const markAll = async () => {
    setLoading(true);
    try {
      await api.patch("/notifications/read-all");
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
      toast.success("Toutes les notifications ont été marquées comme lues");
    } catch {
      toast.error("Impossible de tout marquer comme lu");
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (n) => {
    if (!n.read) await markOne(n.id);
    setOpen(false);
    navigate(extractLink(n));
  };

  const toggle = () => {
    setOpen((v) => {
      if (!v) fetchNotifs(); // refresh on open
      return !v;
    });
  };

  const recent = items.slice(0, 10);

  return (
    <div ref={wrapRef} style={{ position: "relative" }} data-testid="notification-center">
      <button
        type="button"
        className="afr-topbar__icon"
        aria-label="Centre de notifications"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        data-testid="topbar-notifications"
        style={{ position: "relative" }}
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            aria-label={`${unread} notifications non lues`}
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: "0 5px",
              borderRadius: 9,
              background: "#B91C1C",
              color: "white",
              fontSize: 10,
              fontWeight: 700,
              display: "grid",
              placeItems: "center",
              boxShadow: "0 0 0 2px var(--afr-surface, #fff)",
              lineHeight: 1,
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          data-testid="notification-dropdown"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 380,
            maxWidth: "calc(100vw - 24px)",
            background: "var(--afr-surface, #fff)",
            border: "1px solid var(--afr-border, #e5e7eb)",
            borderRadius: 14,
            boxShadow: "0 12px 40px rgba(15,42,66,0.18)",
            zIndex: 60,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "min(70vh, 560px)",
          }}
        >
          <header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "12px 14px",
              borderBottom: "1px solid var(--afr-border,#eee)",
              background: "linear-gradient(180deg,rgba(0,150,64,0.06),transparent)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Bell size={16} />
              <strong style={{ fontSize: "0.95rem" }}>Notifications</strong>
              {unread > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "rgba(185,28,28,0.10)",
                    color: "#B91C1C",
                    fontWeight: 600,
                  }}
                >
                  {unread} non {unread === 1 ? "lue" : "lues"}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAll}
                  disabled={loading}
                  data-testid="notif-mark-all-dropdown"
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "var(--afr-primary,#009640)",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Tout marquer lu
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Fermer"
                style={{
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  color: "#64748b",
                  padding: 4,
                  display: "grid",
                  placeItems: "center",
                }}
                data-testid="notif-close"
              >
                <X size={14} />
              </button>
            </div>
          </header>

          <div style={{ overflowY: "auto", flex: 1 }} data-testid="notif-dropdown-list">
            {recent.length === 0 ? (
              <div
                data-testid="notif-dropdown-empty"
                style={{
                  padding: "28px 20px",
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: "0.9rem",
                }}
              >
                <Bell size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
                <div>Vous êtes à jour !</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Aucune nouvelle notification pour le moment.</div>
              </div>
            ) : (
              recent.map((n) => {
                const p = extractPriority(n);
                const meta = PRIORITY_META[p] || PRIORITY_META.info;
                const KindIcon = KIND_ICON[n.kind] || meta.Icon;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleClick(n)}
                    data-testid={`notif-dropdown-item-${n.id}`}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      padding: "12px 14px",
                      width: "100%",
                      textAlign: "left",
                      border: 0,
                      borderBottom: "1px solid var(--afr-border,#f1f5f9)",
                      background: n.read ? "transparent" : "rgba(0,150,64,0.04)",
                      cursor: "pointer",
                      transition: "background 120ms",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(15,42,66,0.05)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = n.read ? "transparent" : "rgba(0,150,64,0.04)"; }}
                  >
                    <span
                      style={{
                        width: 34, height: 34, borderRadius: 10,
                        background: meta.bg, color: meta.color,
                        display: "grid", placeItems: "center", flexShrink: 0,
                      }}
                    >
                      <KindIcon size={16} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                      }}>
                        <strong style={{
                          fontSize: "0.86rem", fontWeight: n.read ? 500 : 700,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          color: "#0F2A42",
                        }}>{n.title}</strong>
                        {!n.read && (
                          <span
                            aria-label="Non lu"
                            style={{ width: 8, height: 8, borderRadius: 4, background: meta.color, flexShrink: 0 }}
                          />
                        )}
                      </span>
                      <span style={{
                        fontSize: "0.8rem", color: "#475569",
                        marginTop: 2, overflow: "hidden", textOverflow: "ellipsis",
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                      }}>
                        {n.body}
                      </span>
                      <span style={{
                        display: "flex", alignItems: "center", gap: 8, marginTop: 6,
                      }}>
                        <span
                          style={{
                            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: 0.4, padding: "1px 7px", borderRadius: 4,
                            background: meta.bg, color: meta.color,
                          }}
                        >
                          {meta.label}
                        </span>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>
                          {relativeTime(n.created_at)}
                        </span>
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <footer
            style={{
              padding: "10px 14px",
              borderTop: "1px solid var(--afr-border,#eee)",
              background: "#fafafa",
              textAlign: "center",
            }}
          >
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              data-testid="notif-see-all"
              style={{
                fontSize: 13, fontWeight: 600,
                color: "var(--afr-primary,#009640)", textDecoration: "none",
              }}
            >
              Voir toutes les notifications →
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}
